// Gazelle's panel: a conversation with the gazelle Edge Function (or the
// demo's prepared answers). She reads with the admin's rights and proposes;
// each proposal is a card, and only the admin's tap runs it, through the same
// src.assign / cancel / remind as the console's buttons, which write the
// journal. Without the Anthropic key she says so plainly instead of pretending.
import { useCallback, useEffect, useRef, useState } from 'react';
import { S } from '../lib/strings';
import { hhmm } from '../lib/format';
import type { Source } from '../lib/api';
import type { GzProposal, GzReply, GzTurn, JournalEntry } from '../lib/types';
import type { Tab } from './Shell';
import { errText } from './OrderActions';
import { Flame, Ic, type IconName } from './Icon';

/** One journal line in words: "Ruben · affecte à Paul N. · MG-8D27B6". */
export function journalLine(j: JournalEntry) {
  const what = j.action === 'assign' ? S.act.log.assign(j.detail.rider ?? '—')
    : j.action === 'cancel' ? S.act.log.cancel(j.detail.reason ? S.act.reasons[j.detail.reason] : '—')
      : j.action === 'papers' ? S.act.log.papers(j.detail.rider ?? '—', !!j.detail.ok)
        : j.action === 'adjust' ? S.act.log.adjust(j.detail.agency ?? '—', j.detail.amount ?? 0)
          : S.act.log.remind + (j.detail.level != null ? ` · ${j.detail.level} %` : '');
  return { what, on: [j.ref, j.customer].filter(Boolean).join(' · ') };
}

/** An answer's text as blocks: paragraphs, "·" or "-" lists, and the closing "Source :" line apart. */
export function answerBlocks(text: string): ({ k: 'p' | 'src'; t: string } | { k: 'ul'; items: string[] })[] {
  const out: ({ k: 'p' | 'src'; t: string } | { k: 'ul'; items: string[] })[] = [];
  for (const raw of text.split('\n')) {
    const l = raw.trim();
    if (!l) { out.push({ k: 'p', t: '' }); continue; }
    if (/^(source|sources)\s?:/i.test(l)) { out.push({ k: 'src', t: l }); continue; }
    const li = l.match(/^[·•\-*]\s+(.*)$/);
    const last = out[out.length - 1];
    if (li) { if (last?.k === 'ul') last.items.push(li[1]); else out.push({ k: 'ul', items: [li[1]] }); continue; }
    if (last?.k === 'p' && last.t) last.t += ' ' + l; else out.push({ k: 'p', t: l });
  }
  return out.filter((b) => b.k !== 'p' || b.t);
}

/** **bold** inside a line, nothing else: the text stays text. */
function Rich({ t }: { t: string }) {
  const parts = t.split(/\*\*(.+?)\*\*/g);
  return <>{parts.map((p, i) => (i % 2 ? <b key={i}>{p}</b> : p))}</>;
}

type Item =
  | { id: number; who: 'u'; text: string }
  | { id: number; who: 'g'; reply?: GzReply; err?: string; pending?: boolean; q: string };

type CardState = { s: 'idle' | 'busy' | 'no' } | { s: 'done'; msg: string; at: string } | { s: 'err'; msg: string };

function Proposal({ p, src, onDone, openOrder }: { p: GzProposal; src: Source; onDone(): void; openOrder(id: string): void }) {
  const [st, setSt] = useState<CardState>({ s: 'idle' });
  const run = async () => {
    setSt({ s: 'busy' });
    try {
      let msg: string;
      if (p.kind === 'assign') { await src.assign(p.order_id, p.rider_id); msg = S.gz.did.assign; }
      else if (p.kind === 'cancel') { await src.cancel(p.order_id, p.reason, p.note); msg = S.gz.did.cancel; }
      else { const r = await src.remind(p.customer_ids); msg = S.gz.did.remind(r.sent, r.skipped); }
      setSt({ s: 'done', msg, at: hhmm(new Date().toISOString()) });
      onDone();
    } catch (e) {
      setSt({ s: 'err', msg: errText(e) });
    }
  };
  const cls = 'prop' + (st.s === 'done' ? ' done' : st.s === 'no' ? ' no' : '');
  return (
    <div className={cls}>
      <b>{S.gz.prop}</b>
      <p>{p.summary}</p>
      {st.s === 'done' ? <div className="pdone"><Ic n="check" s="xs" />{S.gz.done(st.msg, st.at)}</div>
        : st.s === 'no' ? <div className="muted" style={{ fontSize: 13 }}>{S.gz.declined}</div>
          : (
            <>
              {st.s === 'err' && <div className="perr" role="alert">{st.msg}</div>}
              <div className="pa">
                <button className={'btn sm ' + (p.kind === 'cancel' ? 'dline' : 'ok')} disabled={st.s === 'busy'} onClick={run}>{st.s === 'busy' ? '…' : S.gz.ok[p.kind]}</button>
                <button className="btn sm" disabled={st.s === 'busy'} onClick={() => setSt({ s: 'no' })}>{S.gz.notNow}</button>
                {'order_id' in p && <button className="link" style={{ marginLeft: 'auto', fontSize: 12.5 }} onClick={() => openOrder(p.order_id)}>{S.gz.seeOrder}</button>}
              </div>
            </>
          )}
    </div>
  );
}

interface Props {
  open: boolean; onClose(): void; src?: Source; refresh?: Date | null; tab?: Tab;
  /** A question to ask as the panel opens (from the palette or a home card); n changes for each new ask. */
  ask?: { q: string; n: number } | null;
  onChanged?(): void; openOrder?(id: string): void; who?: string;
}

export function Gazelle({ open, onClose, src, refresh, tab, ask, onChanged, openOrder, who }: Props) {
  const [log, setLog] = useState<JournalEntry[] | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [draft, setDraft] = useState('');
  const [tick, setTick] = useState(0);
  const seq = useRef(1);
  const body = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const busy = items.some((i) => i.who === 'g' && i.pending);

  useEffect(() => {
    if (!open || !src) return;
    let live = true;
    src.journal().then((j) => { if (live) setLog(j); }).catch(() => { if (live) setLog([]); });
    return () => { live = false; };
  }, [open, src, refresh, tick]);
  useEffect(() => { setItems([]); }, [src]);
  useEffect(() => { if (open) setTimeout(() => input.current?.focus(), 300); }, [open]);
  useEffect(() => { const b = body.current; if (b) b.scrollTop = b.scrollHeight; }, [items]);

  const itemsRef = useRef(items);
  itemsRef.current = items;
  const send = useCallback((q: string) => {
    const text = q.trim();
    const prev = itemsRef.current;
    if (!text || !src || prev.some((i) => i.who === 'g' && i.pending)) return;
    setDraft('');
    const turns: GzTurn[] = [];
    for (const i of prev) {
      if (i.who === 'u') turns.push({ role: 'user', content: i.text });
      else if (i.reply?.text) turns.push({ role: 'assistant', content: i.reply.text });
    }
    turns.push({ role: 'user', content: text });
    const gid = seq.current + 1;
    seq.current += 2;
    const next: Item[] = [...prev, { id: gid - 1, who: 'u', text }, { id: gid, who: 'g', pending: true, q: text }];
    itemsRef.current = next;
    setItems(next);
    const started = Date.now();
    src.ask(turns, tab).then(
      async (reply) => {
        // The demo answers at once; a beat of "reading" keeps the rhythm of the real thing.
        if (src.demo) await new Promise((r) => setTimeout(r, Math.max(0, 700 - (Date.now() - started))));
        setItems((cur) => cur.map((i) => (i.id === gid ? { ...i, pending: false, reply } : i)));
      },
      (e) => {
        const code = String((e as Error)?.message ?? e);
        setItems((cur) => cur.map((i) => (i.id === gid ? { ...i, pending: false, err: S.gz.err[code] ?? S.gz.err.other } : i)));
      },
    );
  }, [src, tab]);

  const lastAsk = useRef(0);
  useEffect(() => {
    if (!ask || ask.n === lastAsk.current || !open) return;
    lastAsk.current = ask.n;
    setTimeout(() => send(ask.q), 350);
  }, [ask, open, send]);

  const retry = (q: string, id: number) => { itemsRef.current = itemsRef.current.filter((i) => i.id !== id && i.id !== id - 1); send(q); };
  const changed = () => { setTick((t) => t + 1); onChanged?.(); };
  const sugs = S.gz.sugs[tab ?? 'ov'] ?? S.gz.sugs.ov;

  return (
    <aside className={'gz' + (open ? ' on' : '')} aria-hidden={!open} aria-label="Gazelle">
      <div className="gzh">
        <Flame />
        <div style={{ flex: 1 }}><b>Gazelle</b><span>{S.gz.sub}</span></div>
        {items.length > 0 && <button className="xbtn" aria-label={S.gz.newChat} title={S.gz.newChat} onClick={() => setItems([])} disabled={busy}><Ic n="refresh" s="s" /></button>}
        <button className="xbtn" aria-label={S.gz.closeAria} onClick={onClose}><Ic n="x" s="s" /></button>
      </div>
      <details className="gzj">
        <summary><Ic n="doc" s="xs" />{S.gz.journal}{log?.length ? <span className="st grey nodot num" style={{ marginLeft: 'auto' }}>{log.length}</span> : null}</summary>
        {!log?.length ? <div className="muted" style={{ fontSize: 12.5 }}>{S.gz.empty}</div> : (
          <ol className="gzl">
            {log.slice(0, 30).map((j) => {
              const l = journalLine(j);
              return (
                <li key={j.id}><time className="num">{hhmm(j.at)}</time>
                  <div><b>{j.actor}</b> {l.what}{j.detail.note ? <i className="muted"> « {j.detail.note} »</i> : null}{l.on && <span className="muted"> · {l.on}</span>}</div></li>
              );
            })}
          </ol>
        )}
      </details>
      <div className="gzb" ref={body} aria-live="polite">
        <div className="msg-g">
          <div className="tx">{S.gz.hello(who ? who.split(' ')[0] : '')}</div>
          <div className="rules">
            {S.gz.how.map(([i, b, t]) => (<div key={b}><Ic n={i as IconName} s="xs" /><span><b>{b}</b>{t}</span></div>))}
          </div>
          {src?.demo && <div className="src">{S.gz.demoNote}</div>}
          {!items.length && (<><span className="cap" style={{ marginTop: 4 }}>{S.gz.try}</span>
            <div className="sugs">{sugs.map((q) => <button key={q} className="sug" onClick={() => send(q)}>{q}</button>)}</div></>)}
        </div>
        {items.map((i) => i.who === 'u' ? <div key={i.id} className="msg-u">{i.text}</div> : (
          <div key={i.id} className="msg-g">
            {i.pending ? <div className="gzthink"><div className="typing"><i /><i /><i /></div><span>{S.gz.thinking}</span></div>
              : i.err ? (
                <div className="gzerr"><span>{i.err}</span><button className="btn sm" onClick={() => retry(i.q, i.id)}>{S.gz.retry}</button></div>
              ) : i.reply ? (
                <>
                  {answerBlocks(i.reply.text).map((b, k) => b.k === 'ul'
                    ? <ul key={k} className="gzul">{b.items.map((x, n) => <li key={n}><Rich t={x} /></li>)}</ul>
                    : b.k === 'src' ? <div key={k} className="src">{b.t}</div>
                      : <div key={k} className="tx"><Rich t={b.t} /></div>)}
                  {src && i.reply.proposals.map((p, k) => <Proposal key={k} p={p} src={src} onDone={changed} openOrder={(id) => openOrder?.(id)} />)}
                </>
              ) : null}
          </div>
        ))}
        {items.length > 0 && !busy && (
          <div className="sugs">{sugs.filter((q) => !items.some((i) => i.who === 'u' && i.text === q)).slice(0, 2).map((q) => <button key={q} className="sug" onClick={() => send(q)}>{q}</button>)}</div>
        )}
      </div>
      <form className="gzf" onSubmit={(e) => { e.preventDefault(); send(draft); }}>
        <label className="gzin"><span className="sr">{S.gz.placeholder}</span>
          <input ref={input} placeholder={S.gz.placeholder} autoComplete="off" value={draft} onChange={(e) => setDraft(e.target.value)} disabled={!src} maxLength={1000} />
          <button type="submit" aria-label={S.gz.send} disabled={!src || busy || !draft.trim()}><Ic n="send" s="s" /></button>
        </label>
      </form>
    </aside>
  );
}
